<script setup lang="ts">
import { IonPage, IonTabs, IonRouterOutlet, IonTabBar, IonTabButton, IonLabel, IonIcon, IonFabButton } from '@ionic/vue'
import { useI18n } from 'vue-i18n'
import { useTabNavigation } from '@/composables/useTabNavigation'

const { t } = useI18n()

// Use tab navigation composable for all business logic
const {
    showTabBar,
    activeTab,
    isScannerEnabled,
    scannerIcon,
    scannerButtonText,
    scannerButtonClass,
    icons,
    handleTabClick,
    handleScannerClick
} = useTabNavigation()
</script>

<template>
    <IonPage>
        <IonTabs v-if="showTabBar">
            <IonRouterOutlet />

            <IonTabBar slot="bottom">
                <!-- Home Tab -->
                <IonTabButton tab="dashboard" href="/dashboard" data-test="tab-dashboard" @click="handleTabClick">
                    <IonIcon :icon="activeTab === 'Dashboard' ? icons.home : icons.homeOutline" />
                    <IonLabel>{{ t('navbar.home') }}</IonLabel>
                </IonTabButton>

                <!-- History Tab -->
                <IonTabButton tab="history" href="/history" data-test="tab-history" @click="handleTabClick">
                    <IonIcon :icon="activeTab === 'History' ? icons.calendar : icons.calendarOutline" />
                    <IonLabel>{{ t('navbar.history') }}</IonLabel>
                </IonTabButton>

                <!-- Center FAB Button (QR or Bluetooth based on device presence) -->
                <div class="fab-spacer">
                    <IonFabButton :class="scannerButtonClass" :disabled="!isScannerEnabled" data-test="fab-scanner"
                        :aria-label="scannerButtonText" @click="handleScannerClick">
                        <IonIcon :icon="scannerIcon" />
                    </IonFabButton>
                </div>

                <!-- Vacation Tab -->
                <IonTabButton tab="vacation" href="/vacation" data-test="tab-vacation" @click="handleTabClick">
                    <IonIcon :icon="activeTab === 'Vacation' ? icons.boat : icons.boatOutline" />
                    <IonLabel>{{ t('navbar.vacation') }}</IonLabel>
                </IonTabButton>

                <!-- Settings Tab -->
                <IonTabButton tab="settings" href="/settings" data-test="tab-settings" @click="handleTabClick">
                    <IonIcon :icon="activeTab === 'Settings' ? icons.settings : icons.settingsOutline" />
                    <IonLabel>{{ t('navbar.settings') }}</IonLabel>
                </IonTabButton>
            </IonTabBar>
        </IonTabs>

        <!-- Full-screen routes without tabs -->
        <IonRouterOutlet v-else />
    </IonPage>
</template>

<style lang="scss" scoped>
// Tab Bar - Using Ionic component with CSS variables
ion-tab-bar {
    // Ionic CSS custom properties for theming
    --background: var(--warden-container-bg);
    --border: 1px solid var(--warden-border);

    // Custom layout properties
    height: var(--warden-tab-bar-height-icon-only);
    padding-bottom: env(safe-area-inset-bottom, 0px);
    border-top: 1px solid var(--warden-border);
    box-shadow: 0 -2px 6px rgba(37, 83, 185, 0.1);

    // IMPORTANT: !important needed to allow FAB button to overflow above tab bar
    // Without these, the FAB gets clipped by the tab bar container
    overflow: visible !important;
    contain: none !important;
}

// Tab Button - Using Ionic component with CSS variables
ion-tab-button {
    // Ionic CSS custom properties for theming
    --color: var(--warden-text-secondary);
    --color-selected: var(--warden-primary);

    transition: all var(--warden-duration-normal, 300ms) var(--warden-ease-out, ease-out);

    ion-icon {
        font-size: var(--warden-tab-icon-size);
    }

    ion-label {
        // Hide labels on mobile (icons-only) - standard pattern for phone UIs
        display: none;
    }

    // Interactive states
    &:hover:not([disabled]) {
        --color: var(--warden-primary-hover);
    }

    &:active:not([disabled]) {
        transform: scale(0.95);
    }

    &:focus-visible {
        outline: var(--warden-outline-width) solid var(--warden-border-focus);
        outline-offset: calc(var(--warden-outline-width) * -1);
    }
}

// FAB Spacer - Takes space in tab bar like other tabs
.fab-spacer {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: visible;
}

// Center FAB Button - Using Ionic's IonFabButton with theme customization
.center-fab-button {
    // Position within tab bar (not provided by Ionic)
    position: absolute;
    bottom: var(--warden-fab-bottom-offset);
    z-index: 100;

    // Size (IonFabButton doesn't support --width/--height CSS variables)
    width: var(--warden-fab-size);
    height: var(--warden-fab-size);

    // Theme using Ionic's CSS custom properties
    --background: var(--warden-primary);
    --background-hover: var(--warden-primary-hover);
    --background-activated: var(--warden-primary-active);
    --color: var(--warden-text-inverse);
    --box-shadow: 0 4px 16px rgba(var(--warden-primary-rgb, 0, 122, 255), 0.4);
    --transition: all var(--warden-duration-slow) var(--warden-ease-in-out);

    // Icon size (custom requirement for icon-only tabs)
    ion-icon {
        font-size: var(--warden-fab-icon-size);
    }

    // Disabled state styling
    &--disabled {
        --background: var(--warden-secondary);
        --background-hover: var(--warden-secondary);
        --background-activated: var(--warden-secondary);
        opacity: 0.7;
        cursor: not-allowed;
    }
}

// Reduced motion support
@media (prefers-reduced-motion: reduce) {

    ion-tab-button,
    .center-fab-button {
        transition: none;
    }
}
</style>
